import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../api/supabase';
import { ACCOUNTS } from '../../api/accounts';
import {
  TODAY, daysOverdue, ageBucketOf, monthKeyOf, monthLabelOf, quarterOfMonth, lastNMonthKeys, pctChange,
  oldestUnsettledDate, pctColor, DonutGauge, AgingBar, TrendLineChart, QuarterlyBarChart, RankedBarList,
} from './performanceViz';

const PAGE = 1000;
async function fetchAllPaged(build) {
  let all = [], from = 0;
  while (true) {
    const { data, error } = await build().range(from, from + PAGE - 1);
    if (error) throw error;
    if (!data || !data.length) break;
    all = all.concat(data);
    if (data.length < PAGE) break;
    from += PAGE;
  }
  return all;
}

function fmt(n) { return Math.round(n || 0).toLocaleString(); }

export default function StaffPerformance() {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 7) + '-01';
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Raw AR journal lines (all-time, every agent's clients) — kept around so the Total
  // Performance dashboard can derive its trailing-12-month trend, quarterly split, and aging
  // buckets from the same fetch that already powers "Total Outstanding" below.
  const [arAllTimeRaw, setArAllTimeRaw] = useState([]);
  const [clientToAgentMap, setClientToAgentMap] = useState({});

  // Tickets/visas/bookings, unfiltered by date — feeds the trailing-12-month trend chart
  // plus the fixed calendar-month and calendar-year comparisons below (independent of the
  // From/To picker, which only scopes the "Activity This Period" cards and per-agent table).
  const [ticketsAllTimeRaw, setTicketsAllTimeRaw] = useState([]);
  const [visasAllTimeRaw, setVisasAllTimeRaw] = useState([]);
  const [bookingsAllTimeRaw, setBookingsAllTimeRaw] = useState([]);

  // Every non-VOID invoice with a balance still owed, all-time — the raw material for the
  // "Unpaid Invoices" collections list below. Kept as a flat per-invoice list (not rolled
  // up into arAllTimeRaw's per-client AR balance) since chasing payment happens invoice by
  // invoice, not as one lump client balance.
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [showAllUnpaid, setShowAllUnpaid] = useState(false);

  // Which staff member's own Total Performance to show — '' means the company-wide
  // dashboard (every agent combined); otherwise scopes the whole dashboard (activity,
  // collections, trend, quarterly) down to just that one agent's clients, without
  // leaving this page.
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [agentDropdownSort, setAgentDropdownSort] = useState('tls_code');
  const [showFullRanking, setShowFullRanking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      // toExclusive covers the whole "to" day for timestamp columns (created_at) —
      // a plain lte('to') on a date-only string would silently drop that day's rows.
      const toExclusive = new Date(new Date(to).getTime() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

      // "Previous period" for the tickets/visas/packages MoM comparison — the same number of
      // days as the current From/To selection, immediately before it. When From/To is "This
      // Month" that's exactly last calendar month; for any other range it's still a fair
      // like-for-like comparison window of the same length.
      const periodMs = new Date(toExclusive).getTime() - new Date(from).getTime();
      const prevToExclusive = from;
      const prevFrom = new Date(new Date(from).getTime() - periodMs).toISOString().slice(0, 10);

      const [{ data: agents }, clients, quotations, invoices, tickets, visas, bookings, prevTickets, prevVisas, prevBookings, arLinesPeriod, arLinesAllTime, allTickets, allVisas, allBookings, allInvoicesRaw, receiptAllocs, legacyReceiptVouchers] = await Promise.all([
        supabase.from('agents').select('id, tls_code, full_name, is_active').order('tls_code'),
        fetchAllPaged(() => supabase.from('clients').select('id, care_of_agent_id, full_name').not('care_of_agent_id', 'is', null)),
        fetchAllPaged(() => supabase.from('sale_quotations').select('id, agent_ref_id').not('agent_ref_id', 'is', null).gte('created_at', from).lt('created_at', toExclusive)),
        fetchAllPaged(() => supabase.from('invoices').select('id, client_id').neq('status', 'VOID').gte('issued_date', from).lte('issued_date', to)),
        // Tickets/visas/packages sold — attributed via client_id -> clients.care_of_agent_id,
        // same join invoices use just above. Each record's OWN care_of_agent_id column is
        // basically never populated in practice (checked live: 1 of 347 tickets), so filtering
        // on it made every count read as zero. Tickets exclude VOID, matching the agent
        // portal's convention.
        fetchAllPaged(() => supabase.from('tickets').select('id, client_id').neq('ticket_status', 'VOID').gte('created_at', from).lt('created_at', toExclusive)),
        fetchAllPaged(() => supabase.from('visas').select('id, client_id').gte('created_at', from).lt('created_at', toExclusive)),
        fetchAllPaged(() => supabase.from('bookings').select('id, client_id').gte('created_at', from).lt('created_at', toExclusive)),
        fetchAllPaged(() => supabase.from('tickets').select('id, client_id').neq('ticket_status', 'VOID').gte('created_at', prevFrom).lt('created_at', prevToExclusive)),
        fetchAllPaged(() => supabase.from('visas').select('id, client_id').gte('created_at', prevFrom).lt('created_at', prevToExclusive)),
        fetchAllPaged(() => supabase.from('bookings').select('id, client_id').gte('created_at', prevFrom).lt('created_at', prevToExclusive)),
        // Sales (invoiced) / Collection (received) per client — scoped strictly to the AR
        // control account (1030), NOT every party=client line. Client Deposits (2010) also
        // carries party=client tags, but it's a separate liability account with the opposite
        // normal balance — summing it in with AR silently nets deposits against receivables
        // and produces a meaningless blended number. Restricting to AR keeps Sales/Collection
        // as pure invoice-raised / invoice-settled activity, scoped to the period by the
        // journal entry's own date (matches ProfitLoss's approach). This is a FLOW metric
        // (activity during the period), not a balance — a client paying off an old invoice
        // this month shows as this period's collection even though the sale itself was booked
        // in an earlier period, so it can legitimately look "overpaid" for a short window.
        // It is NOT what should drive "Total Outstanding" below.
        fetchAllPaged(() => supabase.from('journal_lines')
          .select('base_debit, base_credit, party_id, journal_entries!inner(status, entry_date)')
          .eq('account_id', ACCOUNTS.AR)
          .eq('party_type', 'client')
          .eq('journal_entries.status', 'POSTED')
          .gte('journal_entries.entry_date', from)
          .lte('journal_entries.entry_date', to)),
        // All-time AR balance per client, unfiltered by date — the actual receivable STOCK/
        // balance figure ("how much is still owed right now"), independent of the period
        // filter above. Same AR-only scoping as the period query, for the same reason. Also
        // the source data for the Total Performance dashboard's trend/quarterly/aging views.
        fetchAllPaged(() => supabase.from('journal_lines')
          .select('base_debit, base_credit, party_id, journal_entries!inner(status, entry_date, is_reversed, reversal_of)')
          .eq('account_id', ACCOUNTS.AR)
          .eq('party_type', 'client')
          .eq('journal_entries.status', 'POSTED')),
        // Same three, unfiltered by date — feeds the Tickets/Visas/Packages trend chart and
        // its fixed calendar-month / calendar-year comparisons, independent of the From/To
        // picker above (which only scopes the period columns and the picker-relative "vs Prev").
        // Attributed via client_id, same reasoning as the period-scoped fetches above.
        fetchAllPaged(() => supabase.from('tickets').select('id, client_id, created_at').neq('ticket_status', 'VOID')),
        fetchAllPaged(() => supabase.from('visas').select('id, client_id, created_at')),
        fetchAllPaged(() => supabase.from('bookings').select('id, client_id, created_at')),
        // All-time, every non-VOID invoice — feeds the "Unpaid Invoices" collections list.
        // Paid amount is looked up in bulk below (receiptAllocs + legacyReceiptVouchers),
        // same two-source pattern InvoiceList.js already uses, rather than N+1-calling
        // getInvoicePaidAmount() per invoice.
        fetchAllPaged(() => supabase.from('invoices').select('id, invoice_number, client_id, issued_date, line_items').neq('status', 'VOID')),
        fetchAllPaged(() => supabase.from('receipt_allocations').select('receipt_voucher_id, invoice_id, amount').not('invoice_id', 'is', null)),
        fetchAllPaged(() => supabase.from('receipt_vouchers').select('id, reference_id, base_amount, amount').eq('reference_type', 'invoice').is('voided_at', null)),
      ]);

      const clientToAgent = {};
      (clients || []).forEach(c => { clientToAgent[c.id] = c.care_of_agent_id; });

      const byAgent = {};
      (agents || []).forEach(a => { byAgent[a.id] = { agent: a, quotations: 0, invoices: 0, tickets: 0, visas: 0, bookings: 0, prevTickets: 0, prevVisas: 0, prevBookings: 0, sales: 0, collection: 0, outstanding: 0, allTimeInvoiced: 0, allTimeCollected: 0 }; });

      (quotations || []).forEach(q => { if (byAgent[q.agent_ref_id]) byAgent[q.agent_ref_id].quotations += 1; });

      (invoices || []).forEach(inv => {
        const agentId = clientToAgent[inv.client_id];
        if (agentId && byAgent[agentId]) byAgent[agentId].invoices += 1;
      });

      (tickets || []).forEach(t => { const a = clientToAgent[t.client_id]; if (a && byAgent[a]) byAgent[a].tickets += 1; });
      (visas || []).forEach(v => { const a = clientToAgent[v.client_id]; if (a && byAgent[a]) byAgent[a].visas += 1; });
      (bookings || []).forEach(b => { const a = clientToAgent[b.client_id]; if (a && byAgent[a]) byAgent[a].bookings += 1; });
      (prevTickets || []).forEach(t => { const a = clientToAgent[t.client_id]; if (a && byAgent[a]) byAgent[a].prevTickets += 1; });
      (prevVisas || []).forEach(v => { const a = clientToAgent[v.client_id]; if (a && byAgent[a]) byAgent[a].prevVisas += 1; });
      (prevBookings || []).forEach(b => { const a = clientToAgent[b.client_id]; if (a && byAgent[a]) byAgent[a].prevBookings += 1; });

      (arLinesPeriod || []).forEach(l => {
        const agentId = clientToAgent[l.party_id];
        if (!agentId || !byAgent[agentId]) return;
        byAgent[agentId].sales += parseFloat(l.base_debit || 0);
        byAgent[agentId].collection += parseFloat(l.base_credit || 0);
      });

      (arLinesAllTime || []).forEach(l => {
        const agentId = clientToAgent[l.party_id];
        if (!agentId || !byAgent[agentId]) return;
        const dr = parseFloat(l.base_debit || 0), cr = parseFloat(l.base_credit || 0);
        byAgent[agentId].outstanding += dr - cr;
        byAgent[agentId].allTimeInvoiced += dr;
        byAgent[agentId].allTimeCollected += cr;
      });

      const clientNameOf = {};
      (clients || []).forEach(c => { clientNameOf[c.id] = c.full_name; });

      // Paid amount per invoice = new-style receipt_allocations + legacy single-reference
      // receipt_vouchers rows not yet migrated to allocations (same dedup InvoiceList.js
      // uses, to avoid double-counting a voucher both ways).
      const allocatedVoucherIds = new Set((receiptAllocs || []).map(a => a.receipt_voucher_id));
      const paidMap = {};
      (receiptAllocs || []).forEach(a => { paidMap[a.invoice_id] = (paidMap[a.invoice_id] || 0) + (parseFloat(a.amount) || 0); });
      (legacyReceiptVouchers || []).forEach(r => {
        if (!r.reference_id || allocatedVoucherIds.has(r.id)) return;
        paidMap[r.reference_id] = (paidMap[r.reference_id] || 0) + (parseFloat(r.base_amount) || parseFloat(r.amount) || 0);
      });

      const unpaid = (allInvoicesRaw || []).map(inv => {
        const total = (inv.line_items || []).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
        const paid = paidMap[inv.id] || 0;
        return { ...inv, total, paid, balance: total - paid, agentId: clientToAgent[inv.client_id], clientName: clientNameOf[inv.client_id] || '' };
      }).filter(inv => inv.balance > 0.01 && inv.agentId)
        .sort((a, b) => (a.issued_date || '').localeCompare(b.issued_date || ''));

      setRows(Object.values(byAgent));
      setArAllTimeRaw(arLinesAllTime || []);
      setClientToAgentMap(clientToAgent);
      setTicketsAllTimeRaw(allTickets || []);
      setVisasAllTimeRaw(allVisas || []);
      setBookingsAllTimeRaw(allBookings || []);
      setUnpaidInvoices(unpaid);
    } catch (err) {
      console.error('StaffPerformance load error:', err);
      setLoadError(err.message || 'Failed to load');
    }
    setLoading(false);
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  // Collection % is deliberately all-time (allTimeCollected / allTimeInvoiced), not scoped to
  // the period picker — "of everything ever invoiced to this agent's clients, how much has
  // actually been collected". A period-scoped version breaks the moment a client settles an
  // old invoice within a period that raised little new business, producing meaningless numbers
  // like 2900% (period collection far exceeding period sales purely from timing).
  const collPctOf = (r) => r.allTimeInvoiced > 0 ? (r.allTimeCollected / r.allTimeInvoiced * 100) : null;

  const visible = rows.filter(r => includeInactive || r.agent.is_active);

  const totals = visible.reduce((acc, r) => ({
    quotations: acc.quotations + r.quotations,
    invoices: acc.invoices + r.invoices,
    tickets: acc.tickets + r.tickets,
    visas: acc.visas + r.visas,
    bookings: acc.bookings + r.bookings,
    prevTickets: acc.prevTickets + r.prevTickets,
    prevVisas: acc.prevVisas + r.prevVisas,
    prevBookings: acc.prevBookings + r.prevBookings,
    sales: acc.sales + r.sales,
    collection: acc.collection + r.collection,
    outstanding: acc.outstanding + r.outstanding,
    allTimeInvoiced: acc.allTimeInvoiced + r.allTimeInvoiced,
    allTimeCollected: acc.allTimeCollected + r.allTimeCollected,
  }), { quotations: 0, invoices: 0, tickets: 0, visas: 0, bookings: 0, prevTickets: 0, prevVisas: 0, prevBookings: 0, sales: 0, collection: 0, outstanding: 0, allTimeInvoiced: 0, allTimeCollected: 0 });

  // ---- Total Performance dashboard — company-wide, or scoped to one selected agent's
  // clients — derived from the same all-time AR lines fetched above, so switching views (or
  // switching agents) costs no extra round trip. Monthly/quarterly are trailing-12-months /
  // current-year, same convention as an individual agent's tab, and independent of the
  // From/To picker above (which only scopes the table's period columns).
  const companyPerf = useMemo(() => {
    if (!arAllTimeRaw.length) return null;
    const arRows = arAllTimeRaw
      .filter(l => !l.journal_entries?.is_reversed && !l.journal_entries?.reversal_of)
      .filter(l => !selectedAgentId || clientToAgentMap[l.party_id] === selectedAgentId)
      .map(l => ({ date: l.journal_entries.entry_date, debit: parseFloat(l.base_debit || 0), credit: parseFloat(l.base_credit || 0), party_id: l.party_id }));

    const monthKeys = lastNMonthKeys(12);
    const monthly = monthKeys.map(k => ({ key: k, label: monthLabelOf(k), sales: 0, collection: 0, tickets: 0, visas: 0, bookings: 0 }));
    const monthIndex = {}; monthly.forEach((m, i) => { monthIndex[m.key] = i; });
    arRows.forEach(l => { const k = monthKeyOf(l.date); if (monthIndex[k] !== undefined) { monthly[monthIndex[k]].sales += l.debit; monthly[monthIndex[k]].collection += l.credit; } });

    // Tickets/Visas/Packages — scoped the same way as AR above (whole company, or just the
    // selected agent), bucketed into the same trailing-12-month array so one chart can plot
    // all three counts alongside each other.
    const scopedTickets = ticketsAllTimeRaw.filter(t => !selectedAgentId || clientToAgentMap[t.client_id] === selectedAgentId);
    const scopedVisas = visasAllTimeRaw.filter(v => !selectedAgentId || clientToAgentMap[v.client_id] === selectedAgentId);
    const scopedBookings = bookingsAllTimeRaw.filter(b => !selectedAgentId || clientToAgentMap[b.client_id] === selectedAgentId);
    scopedTickets.forEach(t => { const k = monthKeyOf(t.created_at); if (monthIndex[k] !== undefined) monthly[monthIndex[k]].tickets += 1; });
    scopedVisas.forEach(v => { const k = monthKeyOf(v.created_at); if (monthIndex[k] !== undefined) monthly[monthIndex[k]].visas += 1; });
    scopedBookings.forEach(b => { const k = monthKeyOf(b.created_at); if (monthIndex[k] !== undefined) monthly[monthIndex[k]].bookings += 1; });

    // Fixed calendar comparisons (independent of the From/To picker): this calendar month vs
    // last calendar month, and this year-to-date vs the same day-of-year last year. These are
    // what actually answer "is this metric up or down", since the picker-relative "vs Prev"
    // used elsewhere on this page changes meaning depending on what range is selected.
    const thisMonthCounts = monthly[monthly.length - 1], lastMonthCounts = monthly[monthly.length - 2];
    const monthly_cmp = {
      tickets: { curr: thisMonthCounts.tickets, prev: lastMonthCounts.tickets },
      visas: { curr: thisMonthCounts.visas, prev: lastMonthCounts.visas },
      bookings: { curr: thisMonthCounts.bookings, prev: lastMonthCounts.bookings },
    };
    const ytdJan1This = `${TODAY.getFullYear()}-01-01`;
    const ytdJan1Last = `${TODAY.getFullYear() - 1}-01-01`;
    const ytdCutoffLast = `${TODAY.getFullYear() - 1}-${String(TODAY.getMonth() + 1).padStart(2, '0')}-${String(TODAY.getDate()).padStart(2, '0')}`;
    const yoyCount = (list) => {
      let curr = 0, prev = 0;
      list.forEach(r => {
        const d = r.created_at.slice(0, 10);
        if (d >= ytdJan1This) curr += 1;
        else if (d >= ytdJan1Last && d <= ytdCutoffLast) prev += 1;
      });
      return { curr, prev };
    };
    const annual_cmp = { tickets: yoyCount(scopedTickets), visas: yoyCount(scopedVisas), bookings: yoyCount(scopedBookings) };

    const sortedAr = [...arRows].sort((a, b) => a.date.localeCompare(b.date));
    let cumDebit = 0, cumCredit = 0, arPtr = 0;
    monthly.forEach(m => {
      const boundary = `${m.key}-31`;
      while (arPtr < sortedAr.length && sortedAr[arPtr].date <= boundary) { cumDebit += sortedAr[arPtr].debit; cumCredit += sortedAr[arPtr].credit; arPtr++; }
      m.cumCollectionPct = cumDebit > 0 ? (cumCredit / cumDebit) * 100 : null;
    });

    const thisYear = TODAY.getFullYear();
    const quarterly = [1, 2, 3, 4].map(q => ({ q, sales: 0, collection: 0 }));
    arRows.forEach(l => {
      const d = new Date(l.date);
      if (d.getFullYear() === thisYear) { const qq = quarterly[quarterOfMonth(d.getMonth() + 1) - 1]; qq.sales += l.debit; qq.collection += l.credit; }
    });

    const perClient = {};
    arRows.forEach(l => {
      if (!perClient[l.party_id]) perClient[l.party_id] = { debit: 0, credit: 0, lines: [] };
      const c = perClient[l.party_id];
      c.debit += l.debit; c.credit += l.credit; c.lines.push(l);
    });
    const currentBalance = Object.values(perClient).reduce((s, c) => s + (c.debit - c.credit), 0);
    const ninetyDaysAgo = new Date(TODAY.getTime() - 90 * 86400000).toISOString().slice(0, 10);
    const sales90 = arRows.filter(l => l.date >= ninetyDaysAgo).reduce((s, l) => s + l.debit, 0);
    const dso = sales90 > 0 ? (currentBalance / sales90) * 90 : null;

    const aging = { b0_30: 0, b31_60: 0, b61_90: 0, b90plus: 0 };
    let overdueCount = 0, overdueTotal = 0;
    Object.values(perClient).forEach(c => {
      const balance = c.debit - c.credit;
      if (balance <= 0.01) return;
      const asOf = oldestUnsettledDate(c.lines) || TODAY.toISOString().slice(0, 10);
      const days = daysOverdue(asOf);
      const bucket = ageBucketOf(days);
      if (bucket === '0-30') aging.b0_30 += balance;
      else if (bucket === '31-60') aging.b31_60 += balance;
      else if (bucket === '61-90') aging.b61_90 += balance;
      else aging.b90plus += balance;
      if (bucket !== '0-30') { overdueCount += 1; overdueTotal += balance; }
    });

    return { monthly, quarterly, currentBalance, dso, aging, overdueCount, overdueTotal, monthly_cmp, annual_cmp };
  }, [arAllTimeRaw, ticketsAllTimeRaw, visasAllTimeRaw, bookingsAllTimeRaw, selectedAgentId, clientToAgentMap]);

  // Activity + collection-rate figures for whichever scope is selected — the same per-agent
  // row already computed for the table when one agent is picked, or the full aggregate
  // otherwise. Keeps the dashboard's stat cards in sync with the dropdown with no new fetch.
  const selectedAgentRow = selectedAgentId ? rows.find(r => r.agent.id === selectedAgentId) : null;
  const activityStats = selectedAgentRow || totals;
  const overallCollPct = activityStats.allTimeInvoiced > 0 ? (activityStats.allTimeCollected / activityStats.allTimeInvoiced * 100) : null;
  const scopeLabel = selectedAgentRow ? `${selectedAgentRow.agent.tls_code} · ${selectedAgentRow.agent.full_name}` : 'Company-Wide';

  // Small "▲ 12%" / "▼ 8%" badge, reused for every MoM comparison on this page — vs the
  // previous period of the same length (so "This Month" compares to last calendar month).
  const ChangeBadge = ({ curr, prev }) => {
    const change = pctChange(curr, prev);
    if (change === null) return <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>;
    return (
      <span style={{ fontSize: 11, fontWeight: 700, color: change >= 0 ? 'var(--success)' : 'var(--danger)' }}>
        {change >= 0 ? '▲' : '▼'} {Math.abs(change).toFixed(0)}%
      </span>
    );
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Staff Performance</div>
          <div className="page-subtitle">Quotations, invoices, tickets/visas/packages sold, and sales vs. collection by TLS code</div>
        </div>
      </div>

      <div className="card" style={{ padding: '14px 16px', marginBottom: 16, display: 'flex', gap: 16, alignItems: 'end', flexWrap: 'wrap' }}>
        <div><label className="form-label">From</label><input className="form-input" type="date" value={from} onChange={e => setFrom(e.target.value)} /></div>
        <div><label className="form-label">To</label><input className="form-input" type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
        <button className="btn btn-secondary btn-sm" style={{ marginBottom: 1 }} onClick={() => { setFrom(firstOfMonth); setTo(today); }}>
          This Month
        </button>
        <button className="btn btn-secondary btn-sm" style={{ marginBottom: 1 }} onClick={() => { setFrom('2000-01-01'); setTo(today); }}>
          All Time
        </button>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', paddingBottom: 8 }}>
          <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)} />
          Include inactive agents
        </label>
      </div>

      {loadError && <div style={{ color: 'var(--danger)', marginBottom: 14, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 7 }}>{loadError}</div>}

      {loading ? <div className="spinner" /> : (
        // ---- Total Performance (company-wide dashboard) ----
        !companyPerf ? <div className="card" style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>No data yet</div> : (
          <div>
            {/* Switch whose performance this whole dashboard shows — the whole company, or
                drill into one staff member without leaving this page. */}
            <div className="card" style={{ padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <label className="form-label" style={{ margin: 0 }}>Viewing</label>
              <select className="form-input" style={{ maxWidth: 320 }} value={selectedAgentId} onChange={e => setSelectedAgentId(e.target.value)}>
                <option value="">All Agents (Company-Wide)</option>
                {[...visible].sort((a, b) => {
                  if (agentDropdownSort === 'name') return (a.agent.full_name || '').localeCompare(b.agent.full_name || '');
                  if (agentDropdownSort === 'sales') return b.sales - a.sales;
                  if (agentDropdownSort === 'outstanding') return b.outstanding - a.outstanding;
                  if (agentDropdownSort === 'collection_pct') return ((collPctOf(b) ?? -1) - (collPctOf(a) ?? -1));
                  if (agentDropdownSort === 'tickets') return b.tickets - a.tickets;
                  if (agentDropdownSort === 'visas') return b.visas - a.visas;
                  if (agentDropdownSort === 'bookings') return b.bookings - a.bookings;
                  return (a.agent.tls_code || '').localeCompare(b.agent.tls_code || '');
                }).map(r => (
                  <option key={r.agent.id} value={r.agent.id}>{r.agent.tls_code} · {r.agent.full_name}{!r.agent.is_active ? ' (inactive)' : ''}</option>
                ))}
              </select>
              <label className="form-label" style={{ margin: 0 }}>Rank agents by</label>
              <select className="form-input" style={{ maxWidth: 220 }} value={agentDropdownSort} onChange={e => setAgentDropdownSort(e.target.value)}>
                <option value="tls_code">TLS Code (list order only)</option>
                <option value="name">Name (list order only)</option>
                <option value="sales">Sales (Period)</option>
                <option value="outstanding">Outstanding (All-Time)</option>
                <option value="collection_pct">Collection % (All-Time)</option>
                <option value="tickets">Tickets Sold (Period)</option>
                <option value="visas">Visas Issued (Period)</option>
                <option value="bookings">Packages Booked (Period)</option>
              </select>
              {selectedAgentId && <button className="btn btn-secondary btn-sm" onClick={() => setSelectedAgentId('')}>Back to Company-Wide</button>}
            </div>

            {/* Best / Worst only makes sense when comparing agents against each other, so it's
                company-wide-only — hidden once a single agent is selected above. Ranked by
                whatever's picked in "Rank agents by"; only agents with some activity this
                period are ranked at all — an agent with nothing to show isn't "worst", they're
                just idle, and including them would just fill the Worst list with noise. Bar
                color is each agent's all-time collection % (green = healthy, red = chasing
                money), so a high-sales agent who never gets paid still stands out. */}
            {selectedAgentId ? (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '16px 0' }}>
                Best/Worst rankings compare agents against each other — switch back to "All Agents (Company-Wide)" above to see them.
              </div>
            ) : (() => {
              const active = visible.filter(r => r.quotations + r.invoices + r.tickets + r.visas + r.bookings > 0 || r.sales > 0);

              // "Rank agents by" drives this ranking directly — for Outstanding, LOWER is
              // better (owing less money is good), so that one metric sorts ascending for
              // "best" while the other three sort descending. TLS Code / Name have no real
              // best/worst direction; they just fall back to Sales so the panels still show
              // something sensible if picked.
              const metricOf = (r) => {
                if (agentDropdownSort === 'outstanding') return r.outstanding;
                if (agentDropdownSort === 'collection_pct') return collPctOf(r) ?? -1;
                if (agentDropdownSort === 'tickets') return r.tickets;
                if (agentDropdownSort === 'visas') return r.visas;
                if (agentDropdownSort === 'bookings') return r.bookings;
                return r.sales;
              };
              const lowerIsBetter = agentDropdownSort === 'outstanding';
              const bestFirst = [...active].sort((a, b) => lowerIsBetter ? metricOf(a) - metricOf(b) : metricOf(b) - metricOf(a));
              const worstFirst = [...active].sort((a, b) => lowerIsBetter ? metricOf(b) - metricOf(a) : metricOf(a) - metricOf(b));

              const metricLabel = agentDropdownSort === 'outstanding' ? 'Outstanding, All-Time'
                : agentDropdownSort === 'collection_pct' ? 'Collection %, All-Time'
                : agentDropdownSort === 'tickets' ? 'Tickets Sold, Period'
                : agentDropdownSort === 'visas' ? 'Visas Issued, Period'
                : agentDropdownSort === 'bookings' ? 'Packages Booked, Period'
                : 'Sales, Period';
              const formatMetric = agentDropdownSort === 'collection_pct' ? (v => `${v.toFixed(0)}%`) : fmt;

              // Bars are shrunk (see RankedBarList's renderExtra) to make room for each
              // agent's own tickets/visas/packages counts right in the row — no need to
              // cross-reference the table below to see what's behind a Sales figure.
              const toAllItems = (list) => list.map(r => ({
                label: r.agent.full_name, value: metricOf(r), collPct: collPctOf(r),
                tickets: r.tickets, visas: r.visas, bookings: r.bookings,
                prevTickets: r.prevTickets, prevVisas: r.prevVisas, prevBookings: r.prevBookings,
              }));
              const toItems = (list) => toAllItems(list.slice(0, 5));
              // "vs previous month" here is the same picker-relative previous-period figure
              // already computed per agent for the cards above (prevTickets/prevVisas) — when
              // the From/To picker is on "This Month" (the default), that previous period IS
              // last calendar month.
              const renderCounts = (item) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                  <span>Tkt {item.tickets} <ChangeBadge curr={item.tickets} prev={item.prevTickets} /></span>
                  <span>Visa {item.visas} <ChangeBadge curr={item.visas} prev={item.prevVisas} /></span>
                  <span>Pkg {item.bookings} <ChangeBadge curr={item.bookings} prev={item.prevBookings} /></span>
                </span>
              );
              return (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>🏆 Best Performing Agents (by {metricLabel})</div>
                      <div className="card" style={{ padding: '16px 18px' }}>
                        <RankedBarList items={toItems(bestFirst)} valueKey="value" labelKey="label" colorKey="collPct" formatValue={formatMetric} renderExtra={renderCounts} />
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>⚠ Worst Performing Agents (by {metricLabel})</div>
                      <div className="card" style={{ padding: '16px 18px' }}>
                        <RankedBarList items={toItems(worstFirst)} valueKey="value" labelKey="label" colorKey="collPct" formatValue={formatMetric} renderExtra={renderCounts} />
                      </div>
                    </div>
                  </div>

                  <div style={{ textAlign: 'center', marginBottom: showFullRanking ? 10 : 0 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setShowFullRanking(v => !v)}>
                      {showFullRanking ? 'Hide full ranking' : `Show all ${active.length} agents ranked`}
                    </button>
                  </div>

                  {showFullRanking && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>All Agents, Best to Worst (by {metricLabel})</div>
                      <div className="card" style={{ padding: '16px 18px' }}>
                        <RankedBarList items={toAllItems(bestFirst)} valueKey="value" labelKey="label" colorKey="collPct" formatValue={formatMetric} renderExtra={renderCounts} maxItems={active.length} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 24, marginTop: 8 }}>
              Best/Worst are ranked by whatever's picked in "Rank agents by" above (Sales, Outstanding, or Collection %), among agents with some activity this period — an agent with nothing to show is left out rather than counted as "worst". Bar color is always that agent's all-time Collection % (green = healthy, amber/red = chasing payment), so a big seller who doesn't get paid still stands out even when ranking by Sales. Tkt/Visa/Pkg are that agent's tickets/visas/packages sold this period.
            </div>

            {/* Activity — quotations/invoices/tickets/visas/packages, respects the period picker & inactive toggle above */}
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Activity This Period — {scopeLabel}</div>
            <div className="stats-grid" style={{ marginBottom: 24 }}>
              <div className="stat-card"><div className="stat-label">Quotations Raised</div><div className="stat-value">{activityStats.quotations}</div></div>
              <div className="stat-card"><div className="stat-label">Invoices Raised</div><div className="stat-value">{activityStats.invoices}</div></div>
              <div className="stat-card">
                <div className="stat-label">Tickets Sold</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <div className="stat-value">{activityStats.tickets}</div>
                  <ChangeBadge curr={activityStats.tickets} prev={activityStats.prevTickets} />
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 10.5, color: 'var(--text-muted)' }}>
                  <span>MoM <ChangeBadge curr={companyPerf.monthly_cmp.tickets.curr} prev={companyPerf.monthly_cmp.tickets.prev} /></span>
                  <span>YoY <ChangeBadge curr={companyPerf.annual_cmp.tickets.curr} prev={companyPerf.annual_cmp.tickets.prev} /></span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Visas Issued</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <div className="stat-value">{activityStats.visas}</div>
                  <ChangeBadge curr={activityStats.visas} prev={activityStats.prevVisas} />
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 10.5, color: 'var(--text-muted)' }}>
                  <span>MoM <ChangeBadge curr={companyPerf.monthly_cmp.visas.curr} prev={companyPerf.monthly_cmp.visas.prev} /></span>
                  <span>YoY <ChangeBadge curr={companyPerf.annual_cmp.visas.curr} prev={companyPerf.annual_cmp.visas.prev} /></span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Packages Booked</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <div className="stat-value">{activityStats.bookings}</div>
                  <ChangeBadge curr={activityStats.bookings} prev={activityStats.prevBookings} />
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 10.5, color: 'var(--text-muted)' }}>
                  <span>MoM <ChangeBadge curr={companyPerf.monthly_cmp.bookings.curr} prev={companyPerf.monthly_cmp.bookings.prev} /></span>
                  <span>YoY <ChangeBadge curr={companyPerf.annual_cmp.bookings.curr} prev={companyPerf.annual_cmp.bookings.prev} /></span>
                </div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: -16, marginBottom: 24 }}>
              The badge next to each count is vs the previous period of the same length as the From/To picker above. "MoM" and "YoY" underneath are fixed, independent of that picker: this calendar month vs last calendar month, and this year-to-date vs the same date last year.
            </div>

            {/* Tickets/Visas/Packages — trailing 12 months, same trend-chart component as
                Sales vs Collection below, just plotting counts instead of currency. */}
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              Tickets / Visas / Packages — Trailing 12 Months ({scopeLabel})
            </div>
            <TrendLineChart
              monthly={companyPerf.monthly}
              series={[
                { key: 'tickets', color: 'var(--primary)', label: 'Tickets' },
                { key: 'visas', color: 'var(--success)', label: 'Visas' },
                { key: 'bookings', color: '#f59e0b', label: 'Packages' },
              ]}
              formatValue={(v) => Math.round(v).toLocaleString()}
            />

            {/* Per-staff breakdown, right here in Total Performance rather than the separate
                Table View — tickets/visas/packages sold by each agent this period, with the
                same vs-previous-period % change as the cards above. Hidden when a single agent
                is selected above since it's inherently a compare-across-staff view. */}
            {!selectedAgentId && (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Tickets / Visas / Packages by Staff</div>
                <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                          <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Staff</th>
                          <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Tickets</th>
                          <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>vs Prev</th>
                          <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Visas</th>
                          <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>vs Prev</th>
                          <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Packages</th>
                          <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>vs Prev</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...visible]
                          .filter(r => r.tickets + r.visas + r.bookings + r.prevTickets + r.prevVisas + r.prevBookings > 0)
                          .sort((a, b) => (b.tickets + b.visas + b.bookings) - (a.tickets + a.visas + a.bookings))
                          .map(r => (
                            <tr key={r.agent.id} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                              onClick={() => setSelectedAgentId(r.agent.id)}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                              onMouseLeave={e => e.currentTarget.style.background = ''}>
                              <td style={{ padding: '8px 16px' }}>
                                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary)', marginRight: 8 }}>{r.agent.tls_code}</span>
                                {r.agent.full_name}
                              </td>
                              <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600 }}>{r.tickets}</td>
                              <td style={{ padding: '8px 16px', textAlign: 'right' }}><ChangeBadge curr={r.tickets} prev={r.prevTickets} /></td>
                              <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600 }}>{r.visas}</td>
                              <td style={{ padding: '8px 16px', textAlign: 'right' }}><ChangeBadge curr={r.visas} prev={r.prevVisas} /></td>
                              <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600 }}>{r.bookings}</td>
                              <td style={{ padding: '8px 16px', textAlign: 'right' }}><ChangeBadge curr={r.bookings} prev={r.prevBookings} /></td>
                            </tr>
                          ))}
                        {visible.every(r => r.tickets + r.visas + r.bookings + r.prevTickets + r.prevVisas + r.prevBookings === 0) && (
                          <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>No tickets, visas, or packages this period or the one before it.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: -16, marginBottom: 24 }}>
                  "vs Prev" compares this period to the one immediately before it, same length (so "This Month" compares to last calendar month). Click a row to drill into that agent's own Total Performance. Agents with nothing in either period are hidden.
                </div>
              </>
            )}

            {/* Collections: gauge + DSO + overdue summary — all-time, independent of the period picker */}
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Collections — {selectedAgentId ? `${scopeLabel}'s Clients` : 'All Clients'}, All-Time</div>
            <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 28, padding: '18px 24px', marginBottom: 16, flexWrap: 'wrap' }}>
              <DonutGauge pct={overallCollPct} color={pctColor(overallCollPct)} label="Collection Rate" />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, flex: 1 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Days Sales Outstanding</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{companyPerf.dso === null ? '—' : companyPerf.dso.toFixed(0)}{companyPerf.dso !== null && <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-muted)' }}> days</span>}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Overdue Clients</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: companyPerf.overdueCount > 0 ? 'var(--danger)' : 'var(--success)' }}>{companyPerf.overdueCount}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Overdue Amount</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: companyPerf.overdueTotal > 0 ? 'var(--danger)' : 'var(--success)' }}>{fmt(companyPerf.overdueTotal)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Current Balance</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(companyPerf.currentBalance)}</div>
                </div>
              </div>
            </div>

            <AgingBar aging={companyPerf.aging} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 24 }}>
              {[
                { label: '0–30 Days (Current)', value: companyPerf.aging.b0_30, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
                { label: '31–60 Days', value: companyPerf.aging.b31_60, color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
                { label: '61–90 Days', value: companyPerf.aging.b61_90, color: '#ea580c', bg: '#fff7ed', border: '#fed7aa' },
                { label: '90+ Days (Overdue)', value: companyPerf.aging.b90plus, color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
              ].map(c => (
                <div key={c.label} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: c.color, marginBottom: 4 }}>{c.label}</div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: c.color }}>{fmt(c.value)}</div>
                </div>
              ))}
            </div>

            {/* Unpaid Invoices — a ready-made collections push list, oldest first. Scoped to
                whichever agent is selected above, or company-wide with an Agent column. */}
            {(() => {
              const scoped = unpaidInvoices.filter(inv => !selectedAgentId || inv.agentId === selectedAgentId);
              const totalOwed = scoped.reduce((s, inv) => s + inv.balance, 0);
              const visibleRows = showAllUnpaid ? scoped : scoped.slice(0, 20);
              return (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                    Unpaid Invoices — {selectedAgentId ? `${scopeLabel}'s Clients` : 'All Clients'}, All-Time ({scoped.length}, {fmt(totalOwed)} owed)
                  </div>
                  <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 8 }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                            <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Invoice</th>
                            <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Client</th>
                            {!selectedAgentId && <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Agent</th>}
                            <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Issued</th>
                            <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total</th>
                            <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Paid</th>
                            <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Balance</th>
                            <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Days Out</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleRows.map(inv => {
                            const days = daysOverdue(inv.issued_date);
                            const agentRow = rows.find(r => r.agent.id === inv.agentId);
                            return (
                              <tr key={inv.id} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                                onClick={() => window.open(`/invoices/${inv.id}`, '_blank')}
                                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                                onMouseLeave={e => e.currentTarget.style.background = ''}>
                                <td style={{ padding: '8px 16px', fontWeight: 600, color: 'var(--primary)' }}>{inv.invoice_number}</td>
                                <td style={{ padding: '8px 16px' }}>{inv.clientName}</td>
                                {!selectedAgentId && <td style={{ padding: '8px 16px' }}>{agentRow ? `${agentRow.agent.tls_code} · ${agentRow.agent.full_name}` : '—'}</td>}
                                <td style={{ padding: '8px 16px' }}>{inv.issued_date}</td>
                                <td style={{ padding: '8px 16px', textAlign: 'right' }}>{fmt(inv.total)}</td>
                                <td style={{ padding: '8px 16px', textAlign: 'right', color: 'var(--success)' }}>{fmt(inv.paid)}</td>
                                <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 700 }}>{fmt(inv.balance)}</td>
                                <td style={{ padding: '8px 16px', textAlign: 'right', color: days > 60 ? 'var(--danger)' : days > 30 ? '#d97706' : 'var(--text-muted)', fontWeight: 600 }}>{days}</td>
                              </tr>
                            );
                          })}
                          {scoped.length === 0 && (
                            <tr><td colSpan={selectedAgentId ? 7 : 8} style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>Nothing unpaid — fully collected.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {scoped.length > 20 && (
                    <div style={{ textAlign: 'center', marginBottom: 8 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setShowAllUnpaid(v => !v)}>
                        {showAllUnpaid ? 'Show fewer' : `Show all ${scoped.length} unpaid invoices`}
                      </button>
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 24, marginTop: showAllUnpaid || scoped.length <= 20 ? 8 : 0 }}>
                    Sorted oldest-first (Days Out = days since issued). Click a row to open that invoice. All-time, independent of the From/To picker above.
                  </div>
                </>
              );
            })()}

            {/* Sales vs Collection — trailing 12 months */}
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              Sales vs Collection — Trailing 12 Months ({scopeLabel})
            </div>
            <TrendLineChart monthly={companyPerf.monthly} />

            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>{TODAY.getFullYear()} by Quarter ({scopeLabel})</div>
            <div style={{ marginBottom: 24 }}><QuarterlyBarChart quarterly={companyPerf.quarterly} /></div>

            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Collections figures (gauge, DSO, aging, current balance) are all-time and cover every client company-wide, independent of the From/To picker. "Activity This Period" and the Best/Worst leaderboards above respect the From/To picker and the "Include inactive agents" toggle above.
            </div>
          </div>
        )
      )}
    </div>
  );
}
